import { useState, useEffect, useCallback } from 'react';
import { 
  LayoutDashboard, 
  PieChart, 
  ArrowLeftRight, 
  Upload, 
  AlertTriangle,
  RefreshCw,
  TrendingUp,
  ArrowRightLeft,
  ClipboardList,
  ClipboardCheck,
  Layers,
  LineChart,
  Rocket,
  LogOut,
  X,
  Activity,
} from 'lucide-react';
import Dashboard from './components/Dashboard';
import PositionsTable from './components/PositionsTable';
import AllocationChart from './components/AllocationChart';
import TradeLog from './components/TradeLog';
import CsvUpload from './components/CsvUpload';
import RuleAlerts from './components/RuleAlerts';
import TransferTracker from './components/TransferTracker';
import TradePlans from './components/TradePlans';
import TaxLots from './components/TaxLots';
import Performance from './components/Performance';
import DeploymentPlans from './components/DeploymentPlans';
import EarningsScorecards from './components/EarningsScorecards';
import OptionsTable from './components/OptionsTable';
import Market from './components/Market';
import MarketTicker from './components/MarketTicker';
import OscillatorBanner from './components/OscillatorBanner';
import api, { refreshPrices } from './api/client';
import { AuthProvider, useAuth, LoginPage } from './auth';

function formatTimeAgo(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'performance', label: 'Performance', icon: LineChart },
  { id: 'market', label: 'Market', icon: Activity },
  { id: 'positions', label: 'Positions', icon: TrendingUp },
  { id: 'deploy', label: 'Deploy', icon: Rocket },
  { id: 'plans', label: 'Trade Plans', icon: ClipboardList },
  { id: 'allocation', label: 'Allocation', icon: PieChart },
  { id: 'lots', label: 'Tax Lots', icon: Layers },
  { id: 'trades', label: 'Trades', icon: ArrowLeftRight },
  { id: 'scorecards', label: 'Scorecards', icon: ClipboardCheck },
  { id: 'transfers', label: 'Transfers', icon: ArrowRightLeft },
  { id: 'import', label: 'Import', icon: Upload },
];

/**
 * Main application content (requires authentication)
 */
function AppContent() {
  const { user, logout, isDevMode } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [lastPriceRefresh, setLastPriceRefresh] = useState(null);
  const [, setTick] = useState(0);
  // Bumped on every manual refresh so the global market ticker refetches in lockstep.
  const [marketRefresh, setMarketRefresh] = useState(0);

  // Re-render every 30s to keep the relative timestamp fresh
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = useCallback(async (skipPrices = false) => {
    setMarketRefresh((n) => n + 1);
    setLoading(true);
    setError(null);

    let priceFailed = false;
    if (!skipPrices) {
      setRefreshingPrices(true);
      try {
        await refreshPrices();
        setPriceError(null);
      } catch (err) {
        priceFailed = true;
        console.warn('Price refresh failed:', err);
        setPriceError(`Price refresh failed: ${err.message}`);
      } finally {
        setRefreshingPrices(false);
      }
    }

    try {
      const data = await api.getPortfolioSummary();
      setSummary(data);
      if (data.last_price_refresh) {
        setLastPriceRefresh(data.last_price_refresh);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  const hasViolations = summary?.rule_violations?.length > 0;
  const buttonLabel = refreshingPrices ? 'Refreshing prices...' : loading ? 'Reloading...' : 'Refresh';

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-sky-500/25">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-100">Portfolio Co-Pilot</h1>
                <p className="text-xs text-slate-400">Investment Dashboard</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {hasViolations && (
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-400 text-sm hover:bg-amber-500/30 transition-colors"
                >
                  <AlertTriangle className="w-4 h-4" />
                  {summary.rule_violations.length} Alert{summary.rule_violations.length > 1 ? 's' : ''}
                </button>
              )}
              {lastPriceRefresh && (
                <span className="text-xs text-slate-500 hidden sm:inline">
                  Prices: {formatTimeAgo(lastPriceRefresh)}
                </span>
              )}
              <button
                onClick={() => handleRefresh()}
                disabled={loading}
                className="btn btn-ghost"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {buttonLabel}
              </button>
              
              {/* User info and logout */}
              {!isDevMode && (
                <div className="flex items-center gap-3 pl-3 border-l border-slate-700">
                  <span className="text-sm text-slate-400">{user?.email || user?.username}</span>
                  <button
                    onClick={logout}
                    className="btn btn-ghost text-slate-400 hover:text-red-400"
                    title="Sign out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Market ticker + oscillator banner (global) */}
      <MarketTicker refreshSignal={marketRefresh} />
      <OscillatorBanner onClick={() => setActiveTab('market')} />

      {/* Navigation */}
      <nav className="bg-slate-800/30 border-b border-slate-700/50">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'text-sky-400 border-sky-400 bg-sky-500/10'
                    : 'text-slate-400 border-transparent hover:text-slate-200 hover:bg-slate-700/30'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400">
            <p className="font-medium">Error loading data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {priceError && (
          <div className="mb-6 p-4 bg-amber-500/20 border border-amber-500/30 rounded-xl text-amber-400 flex items-center justify-between">
            <p className="text-sm">{priceError} — Data shown from last known prices.</p>
            <button onClick={() => setPriceError(null)} className="ml-4 p-1 hover:bg-amber-500/20 rounded transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading && !summary ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8"></div>
              <p className="text-slate-400">Loading portfolio data...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <Dashboard summary={summary} onNavigate={setActiveTab} />
            )}
            {activeTab === 'performance' && (
              <Performance />
            )}
            {activeTab === 'market' && (
              <Market />
            )}
            {activeTab === 'positions' && (
              <>
                <PositionsTable positions={summary?.positions || []} />
                <OptionsTable />
              </>
            )}
            {activeTab === 'deploy' && (
              <DeploymentPlans />
            )}
            {activeTab === 'allocation' && (
              <AllocationChart allocation={summary?.allocation || []} totalValue={summary?.total_value || 0} />
            )}
            {activeTab === 'lots' && (
              <TaxLots />
            )}
            {activeTab === 'trades' && (
              <TradeLog initialTrades={summary?.recent_trades || []} />
            )}
            {activeTab === 'plans' && (
              <TradePlans />
            )}
            {activeTab === 'scorecards' && (
              <EarningsScorecards />
            )}
            {activeTab === 'transfers' && (
              <TransferTracker onRefresh={handleRefresh} />
            )}
            {activeTab === 'import' && (
              <CsvUpload onImportComplete={() => handleRefresh(true)} />
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-6 mt-8">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-slate-500">
            Portfolio Co-Pilot • Local-first portfolio management with Claude integration
          </p>
        </div>
      </footer>
    </div>
  );
}

/**
 * App wrapper with authentication
 */
function AuthenticatedApp() {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show main app
  return <AppContent />;
}

/**
 * Root App component with AuthProvider
 */
function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export default App;

